/**
 * PeriodSelector Component
 * 
 * Period selection dropdown and date navigation controls.
 */

import React from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  TextField,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface PeriodSelectorProps {
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  fromDate: string;
  toDate: string;
  onFromDateChange: (date: string) => void;
  onToDateChange: (date: string) => void;
  onPeriodNavigation: (direction: "left" | "right") => void;
}

/**
 * Period selector with date navigation
 */
export const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onPeriodNavigation,
}) => {
  const isAllTime = selectedPeriod === "All time";
  const isCustom = selectedPeriod === "Custom";

  return (
    <>
      {/* @ts-expect-error - MUI Grid item prop types issue in v7 */}
      <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            label="Period"
            onChange={(e) => onPeriodChange(e.target.value)}
            sx={{ width: "100%" }}
          >
            <MenuItem value="All time">All time</MenuItem>
            <MenuItem value="Daily">Daily</MenuItem>
            <MenuItem value="Weekly">Weekly</MenuItem>
            <MenuItem value="Monthly">Monthly</MenuItem>
            <MenuItem value="Yearly">Yearly</MenuItem>
            <MenuItem value="Custom">Custom Period</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      {isCustom && (
        <>
          {/* @ts-expect-error - MUI Grid item prop types issue in v7 */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
              <IconButton
                size="small"
                onClick={() => onPeriodNavigation("left")}
                disabled={true}
                sx={{ 
                  border: 1, 
                  borderColor: "divider", 
                  borderRadius: 1,
                  minWidth: 40,
                  minHeight: 40,
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={fromDate}
                onChange={(e) => onFromDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              />
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={toDate}
                onChange={(e) => onToDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => onPeriodNavigation("right")}
                disabled={true}
                sx={{ 
                  border: 1, 
                  borderColor: "divider", 
                  borderRadius: 1,
                  minWidth: 40,
                  minHeight: 40,
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Grid>
        </>
      )}
      {!isCustom && (
        <>
          {/* @ts-expect-error - MUI Grid item prop types issue in v7 */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: "100%" }}>
              <IconButton
                size="small"
                onClick={() => onPeriodNavigation("left")}
                disabled={isAllTime}
                sx={{ 
                  border: 1, 
                  borderColor: "divider", 
                  borderRadius: 1,
                  minWidth: 40,
                  minHeight: 40,
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={fromDate}
                onChange={(e) => onFromDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              />
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={toDate}
                onChange={(e) => onToDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              />
              <IconButton
                size="small"
                onClick={() => onPeriodNavigation("right")}
                disabled={isAllTime}
                sx={{ 
                  border: 1, 
                  borderColor: "divider", 
                  borderRadius: 1,
                  minWidth: 40,
                  minHeight: 40,
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Grid>
        </>
      )}
    </>
  );
};
